import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { VERTICAL_SWITCH_RESET_KEYS } from '../config/filterConfig';
import { getVerticalOptions } from '../config/taskVerticals';

interface TaskVerticalSwitcherProps {
  currentVertical: string;
}

/**
 * Dropdown switcher for switching between task verticals
 * Navigates between different task views at /tasks
 */
export const TaskVerticalSwitcher = ({
  currentVertical,
}: TaskVerticalSwitcherProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const verticalOptions = getVerticalOptions();
  const selectedVertical = verticalOptions.find(
    (v) => v.id === currentVertical,
  );

  const handleSelect = (verticalId: string) => {
    // External tasks have their own route
    if (verticalId === 'external') {
      navigate('/external-tasks');
      setIsOpen(false);
      return;
    }

    const newParams = new URLSearchParams(searchParams);

    // 1. Reset keys that shouldn't persist across verticals (e.g. pagination)
    // Contextual filters (e.g. assignee) are preserved intentionally.
    VERTICAL_SWITCH_RESET_KEYS.forEach((key) => {
      newParams.delete(key);
    });

    // 2. Update the vertical param
    if (verticalId === 'all') {
      // 'all' is the default, so we can omit the param for cleaner URLs
      newParams.delete('vertical');
    } else {
      newParams.set('vertical', verticalId);
    }

    // 3. Navigate to /tasks with the new params
    const queryString = newParams.toString();
    navigate(queryString ? `/tasks?${queryString}` : '/tasks');
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-md hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
      >
        <span>{selectedVertical?.label || 'All Tasks'}</span>
        <ChevronDownIcon className="size-3.5" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close dropdown"
          />

          {/* Dropdown menu */}
          <div className="absolute left-0 mt-1 w-48 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-md shadow-lg z-20">
            {verticalOptions.map((vertical) => (
              <button
                key={vertical.id}
                type="button"
                onClick={() => handleSelect(vertical.id)}
                className={`
                  w-full text-left px-3 py-1.5 text-xs
                  ${vertical.id === currentVertical ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-700'}
                  transition-colors
                  first:rounded-t-md last:rounded-b-md
                `.trim()}
                title={vertical.description}
              >
                {vertical.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
